import { PixFilterBanner, PixMultiSelect, PixSearchInput, PixSelect, PixStars } from '@1024pix/nebulix-ember';
import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import ENV from 'pix-orga/config/environment';

import DivisionsFilter from '../../ui/divisions-filter';
import GroupsFilter from '../../ui/groups-filter';

const debounceTime = ENV.pagination.debounce;

export default class ParticipationFilters extends Component {
  @service intl;
  @service currentUser;
  @service pixMetrics;

  @tracked stages = [];
  @tracked isStagesLoading = true;
  @tracked badges = [];
  @tracked isBadgesLoading = true;

  constructor() {
    super(...arguments);
    Promise.resolve(this.args.campaign.stages).then((stages) => {
      this.stages = stages;
      this.isStagesLoading = false;
    });
    Promise.resolve(this.args.campaign.badges).then((badges) => {
      this.badges = badges;
      this.isBadgesLoading = false;
    });
  }

  get certificabilityOptions() {
    return [
      {
        value: 'eligible',
        label: this.intl.t('pages.sco-organization-participants.table.column.is-certifiable.eligible'),
      },
      {
        value: 'non-eligible',
        label: this.intl.t('pages.sco-organization-participants.table.column.is-certifiable.non-eligible'),
      },
    ];
  }

  get isClearFiltersButtonDisabled() {
    return (
      !this.args.selectedStatus &&
      (!this.displaySearchFilter || !this.args.searchFilter) &&
      (!this.displayDivisionFilter || this.args.selectedDivisions.length === 0) &&
      (!this.displayGroupsFilter || this.args.selectedGroups.length === 0) &&
      (!this.displayStagesFilter || this.args.selectedStages.length === 0) &&
      (!this.displayBadgesFilter || this.args.selectedBadges.length === 0) &&
      (!this.displayBadgesFilter || this.args.selectedUnacquiredBadges.length === 0) &&
      (!this.displayCertificabilityFilter || !this.args.selectedCertificability) &&
      (!this.displayParticipantExternalIdFilter || !this.args.participantExternalIdFilter)
    );
  }

  get displayFilters() {
    return (
      this.displayStagesFilter ||
      this.displayBadgesFilter ||
      this.displayDivisionFilter ||
      this.displayStatusFilter ||
      this.displayGroupsFilter ||
      this.displayCertificabilityFilter ||
      this.displaySearchFilter ||
      this.displayParticipantExternalIdFilter
    );
  }

  get displayCertificabilityFilter() {
    const { isTypeAssessment, isTypeExam } = this.args.campaign;
    return !isTypeAssessment && !isTypeExam && !this.args.isHiddenCertificability;
  }

  get displayStagesFilter() {
    if (this.isStagesLoading) return false;
    const { isTypeAssessment, isTypeExam, hasStages } = this.args.campaign;
    return !this.args.isHiddenStages && (isTypeAssessment || isTypeExam) && hasStages;
  }

  get displayBadgesFilter() {
    if (this.isBadgesLoading) return false;
    const { isTypeAssessment, isTypeExam, hasBadges } = this.args.campaign;
    return !this.args.isHiddenBadges && (isTypeAssessment || isTypeExam) && hasBadges;
  }

  get displayDivisionFilter() {
    return this.currentUser.isSCOManagingStudents;
  }

  get displayGroupsFilter() {
    return this.currentUser.isSUPManagingStudents && !this.args.isHiddenGroup;
  }

  get displayStatusFilter() {
    return !this.args.isHiddenStatus;
  }

  get displaySearchFilter() {
    return !this.args.isHiddenSearch;
  }

  get displayParticipantExternalIdFilter() {
    return Boolean(this.args.participantExternalIdLabel);
  }

  get stageOptions() {
    const totalStage = this.stages.length - 1;
    return this.stages.map((stage, index) => ({
      value: stage.id,
      reachedStage: index,
      totalStage,
      label: this.intl.t('common.result.stages', { count: index, total: totalStage }),
    }));
  }

  get badgeOptions() {
    return this.badges.map(({ id, title }) => ({ value: id, label: title }));
  }

  get statusOptions() {
    const statuses = ['STARTED', 'SHARED'];

    if (this.currentUser.hasImportFeature) statuses.push('NOT_STARTED');

    return statuses.map((status) => {
      const label = this.intl.t(`components.participation-status.${status}`);
      return { value: status, label };
    });
  }

  @action
  onSelectStage(stages) {
    this.args.onFilter('stages', stages);
  }

  @action
  onSelectGroup(groups) {
    this.args.onFilter('groups', groups);
  }

  @action
  onSelectStatus(status) {
    this.args.onFilter('status', status);
  }

  @action
  onSelectBadge(badges) {
    this.args.onFilter('badges', badges);
  }

  @action
  onSelectUnacquiredBadge(unacquiredBadges) {
    this.args.onFilter('unacquiredBadges', unacquiredBadges);
  }

  @action
  onSelectDivision(divisions) {
    this.args.onFilter('divisions', divisions);
  }

  @action
  onSelectCertificability(certificability) {
    this.args.onFilter('certificability', certificability);
  }

  <template>
    {{#if this.displayFilters}}
      <PixFilterBanner
        @title={{t "common.filters.title"}}
        class="participant-filter-banner hide-on-mobile"
        aria-label={{t "pages.campaign-results.filters.aria-label"}}
        @details={{t "pages.campaign-results.filters.participations-count" count=@rowCount}}
        @clearFiltersLabel={{t "common.filters.actions.clear"}}
        @onClearFilters={{@onResetFilter}}
        @isClearFilterButtonDisabled={{this.isClearFiltersButtonDisabled}}
      >
        {{#if this.displayStatusFilter}}
          <PixSelect
            @screenReaderOnly={{true}}
            @options={{this.statusOptions}}
            @onChange={{this.onSelectStatus}}
            @value={{@selectedStatus}}
            @texts={{hash placeholder=(t "pages.campaign-results.filters.type.status.empty")}}
            @hideDefaultOption={{false}}
          >
            <:label>{{t "pages.campaign-results.filters.type.status.title"}}</:label>
          </PixSelect>
        {{/if}}
        {{#if this.displayDivisionFilter}}
          <DivisionsFilter @model={{@campaign}} @selected={{@selectedDivisions}} @onSelect={{this.onSelectDivision}} />
        {{/if}}
        {{#if this.displayGroupsFilter}}
          <GroupsFilter @campaign={{@campaign}} @onSelect={{this.onSelectGroup}} @selectedGroups={{@selectedGroups}} />
        {{/if}}
        {{#if this.displaySearchFilter}}
          <PixSearchInput
            @id="search"
            value={{@searchFilter}}
            @screenReaderOnly={{true}}
            @placeholder={{t "common.filters.fullname.placeholder"}}
            @debounceTimeInMs={{debounceTime}}
            @triggerFiltering={{@onFilter}}
          >
            <:label>{{t "common.filters.fullname.label"}}</:label>
          </PixSearchInput>
        {{/if}}
        {{#if this.displayParticipantExternalIdFilter}}
          <PixSearchInput
            @id="participantExternalId"
            value={{@participantExternalIdFilter}}
            @screenReaderOnly={{true}}
            @placeholder={{@participantExternalIdLabel}}
            @debounceTimeInMs={{debounceTime}}
            @triggerFiltering={{@onFilter}}
          >
            <:label>{{t "common.filters.participantExternalId.label"}}</:label>
          </PixSearchInput>
        {{/if}}
        {{#if this.displayStagesFilter}}
          <PixMultiSelect
            @screenReaderOnly={{true}}
            @texts={{hash placeholder=(t "pages.campaign-results.filters.type.stages")}}
            @onChange={{this.onSelectStage}}
            @values={{@selectedStages}}
            @options={{this.stageOptions}}
            @className="participant-filter-banner__stages-select"
          >
            <:label>{{t "pages.campaign-results.filters.type.stages"}}</:label>
            <:default as |stage|>
              <PixStars
                @count={{stage.reachedStage}}
                @total={{stage.totalStage}}
                @alt={{stage.label}}
                @color="blue"
                class="participant-filter-banner__stars"
              />
            </:default>
          </PixMultiSelect>
        {{/if}}
        {{#if this.displayBadgesFilter}}
          <PixMultiSelect
            @texts={{hash placeholder=(t "pages.campaign-results.filters.type.badges")}}
            @screenReaderOnly={{true}}
            @onChange={{this.onSelectBadge}}
            @values={{@selectedBadges}}
            @options={{this.badgeOptions}}
            @className="participant-filter-banner__badges"
          >
            <:label>{{t "pages.campaign-results.filters.type.badges"}}</:label>
            <:default as |badge|>{{badge.label}}</:default>
          </PixMultiSelect>

          <PixMultiSelect
            @texts={{hash placeholder=(t "pages.campaign-results.filters.type.unacquired-badges")}}
            @screenReaderOnly={{true}}
            @onChange={{this.onSelectUnacquiredBadge}}
            @values={{@selectedUnacquiredBadges}}
            @options={{this.badgeOptions}}
            @className="participant-filter-banner__badges"
          >
            <:label>{{t "pages.campaign-results.filters.type.unacquired-badges"}}</:label>
            <:default as |badge|>{{badge.label}}</:default>
          </PixMultiSelect>
        {{/if}}
        {{#if this.displayCertificabilityFilter}}
          <PixSelect
            @options={{this.certificabilityOptions}}
            @value={{@selectedCertificability}}
            @onChange={{this.onSelectCertificability}}
            @screenReaderOnly={{true}}
            @texts={{hash placeholder=(t "components.certificability.placeholder-select")}}
          >
            <:label>{{t "pages.sco-organization-participants.filter.certificability.label"}}</:label>
          </PixSelect>
        {{/if}}
      </PixFilterBanner>
    {{/if}}
  </template>
}
